import React, { useEffect, useMemo, useState } from "react";
import axios from "api/axios";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";

const emptyForm = {
  payroll_month: "",
  period_name: "",
  period_start: "",
  period_end: "",
  variation_cutoff_date: "",
  payroll_run_date: "",
  approval_due_date: "",
  payment_due_date: "",
  cycle_type: "monthly",
  status: "draft",
  allow_variations_after_cutoff: false,
  allow_payroll_after_payment_due: false,
  notes: "",
  reason: "",
};

const statusColors = {
  draft: "default",
  open: "success",
  variation_closed: "warning",
  processing: "info",
  approved: "primary",
  paid: "success",
  closed: "default",
  cancelled: "error",
};

function normalizeCalendarPayload(form) {
  const payload = { ...form };
  [
    "variation_cutoff_date",
    "payroll_run_date",
    "approval_due_date",
    "payment_due_date",
    "notes",
    "period_name",
    "reason",
  ].forEach((key) => {
    if (payload[key] === "") payload[key] = null;
  });
  return payload;
}

export default function PayrollCalendar() {
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [year, setYear] = useState(new Date().getFullYear());

  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [calendarRes, alertRes] = await Promise.all([
        axios.get("/payroll/calendar", { params: { per_page: 50 } }),
        axios.get("/payroll/calendar/alerts", { params: { per_page: 20 } }),
      ]);
      setRows(calendarRes.data?.data || calendarRes.data || []);
      setAlerts(alertRes.data?.data || alertRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load payroll calendar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    const start = `${currentMonth}-01`;
    const end = new Date(
      new Date(`${currentMonth}-01`).getFullYear(),
      new Date(`${currentMonth}-01`).getMonth() + 1,
      0
    )
      .toISOString()
      .slice(0, 10);
    setForm({ ...emptyForm, payroll_month: currentMonth, period_start: start, period_end: end });
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...emptyForm,
      ...row,
      period_start: row.period_start?.slice(0, 10) || "",
      period_end: row.period_end?.slice(0, 10) || "",
      variation_cutoff_date: row.variation_cutoff_date?.slice(0, 10) || "",
      payroll_run_date: row.payroll_run_date?.slice(0, 10) || "",
      approval_due_date: row.approval_due_date?.slice(0, 10) || "",
      payment_due_date: row.payment_due_date?.slice(0, 10) || "",
      reason: "",
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = normalizeCalendarPayload(form);
      if (editing?.id) {
        await axios.put(`/payroll/calendar/${editing.id}`, payload);
        setMessage("Payroll calendar updated successfully.");
      } else {
        await axios.post("/payroll/calendar", payload);
        setMessage("Payroll calendar created successfully.");
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          Object.values(e.response?.data?.errors || {})?.[0]?.[0] ||
          "Unable to save payroll calendar."
      );
    } finally {
      setSaving(false);
    }
  };

  const action = async (row, endpoint, success) => {
    setError("");
    setMessage("");
    try {
      await axios.post(`/payroll/calendar/${row.id}/${endpoint}`, { reason: success });
      setMessage(success);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Calendar action failed.");
    }
  };

  const generateYear = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.post("/payroll/calendar/generate-year", { year: Number(year) });
      setMessage(res.data?.message || "Payroll calendar generated.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to generate yearly payroll calendar.");
    } finally {
      setSaving(false);
    }
  };

  const resolveAlert = async (alert) => {
    await axios.post(`/payroll/calendar/alerts/${alert.id}/resolve`);
    await load();
  };

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Payroll Calendar & Scheduling</h2>
          <p style={{ margin: "6px 0 0", color: "#667085" }}>
            Control monthly periods, variation cut-offs, payroll run dates, approval deadlines,
            payment due dates, and alerts.
          </p>
        </div>
        <Button variant="contained" startIcon={<Icon>add</Icon>} onClick={openCreate}>
          New Period
        </Button>
      </div>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2, height: "100%" }}>
            <h3 style={{ marginTop: 0 }}>Generate Yearly Calendar</h3>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                label="Year"
                type="number"
                size="small"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
              <Button variant="outlined" disabled={saving} onClick={generateYear}>
                Generate / Update 12 Months
              </Button>
              <Button variant="text" onClick={load}>
                Refresh
              </Button>
            </div>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, height: "100%" }}>
            <h3 style={{ marginTop: 0 }}>Open Alerts</h3>
            <strong style={{ fontSize: 28 }}>{alerts.length}</strong>
            <p style={{ color: "#667085", marginBottom: 0 }}>
              Unresolved calendar reminders and due-date warnings.
            </p>
          </Card>
        </Grid>
      </Grid>

      {alerts.length > 0 && (
        <Card sx={{ p: 2, mb: 2 }}>
          <h3 style={{ marginTop: 0 }}>Calendar Alerts</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {alerts.map((a) => (
              <Alert
                key={a.id}
                severity={
                  a.severity === "critical"
                    ? "error"
                    : a.severity === "warning"
                    ? "warning"
                    : "info"
                }
                action={
                  <Button color="inherit" size="small" onClick={() => resolveAlert(a)}>
                    Resolve
                  </Button>
                }
              >
                <strong>
                  {a.payroll_month}: {a.title}
                </strong>{" "}
                {a.message} {a.due_date ? `(Due: ${a.due_date})` : ""}
              </Alert>
            ))}
          </div>
        </Card>
      )}

      <Card sx={{ p: 2 }}>
        <h3 style={{ marginTop: 0 }}>Payroll Periods</h3>
        {loading ? (
          <CircularProgress />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
              <thead>
                <tr>
                  {[
                    "Month",
                    "Name",
                    "Period",
                    "Cut-off",
                    "Run",
                    "Approval",
                    "Payment",
                    "Cycle",
                    "Status",
                    "Alerts",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #eee" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ padding: 10 }}>{row.payroll_month}</td>
                    <td style={{ padding: 10 }}>{row.period_name || "-"}</td>
                    <td style={{ padding: 10 }}>
                      {row.period_start} → {row.period_end}
                    </td>
                    <td style={{ padding: 10 }}>{row.variation_cutoff_date || "-"}</td>
                    <td style={{ padding: 10 }}>{row.payroll_run_date || "-"}</td>
                    <td style={{ padding: 10 }}>{row.approval_due_date || "-"}</td>
                    <td style={{ padding: 10 }}>{row.payment_due_date || "-"}</td>
                    <td style={{ padding: 10 }}>{row.cycle_type}</td>
                    <td style={{ padding: 10 }}>
                      <Chip
                        size="small"
                        label={row.status}
                        color={statusColors[row.status] || "default"}
                      />
                    </td>
                    <td style={{ padding: 10 }}>{row.open_alerts_count || 0}</td>
                    <td style={{ padding: 10 }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Button size="small" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        {row.status === "draft" && (
                          <Button
                            size="small"
                            onClick={() => action(row, "open", "Payroll calendar opened.")}
                          >
                            Open
                          </Button>
                        )}
                        {row.status === "open" && (
                          <Button
                            size="small"
                            onClick={() =>
                              action(row, "close-variations", "Variation window closed.")
                            }
                          >
                            Close Variations
                          </Button>
                        )}
                        {row.status === "variation_closed" && (
                          <Button
                            size="small"
                            onClick={() =>
                              action(row, "reopen-variations", "Variation window reopened.")
                            }
                          >
                            Reopen
                          </Button>
                        )}
                        {["approved", "paid"].includes(row.status) && (
                          <Button
                            size="small"
                            onClick={() => action(row, "close", "Payroll period closed.")}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan="11" style={{ padding: 18, textAlign: "center" }}>
                      No payroll calendar periods found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Payroll Calendar" : "Create Payroll Calendar"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Payroll Month"
                type="month"
                value={form.payroll_month}
                onChange={(e) => setForm({ ...form, payroll_month: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Period Name"
                value={form.period_name || ""}
                onChange={(e) => setForm({ ...form, period_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Period Start"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.period_start || ""}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Period End"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.period_end || ""}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Cycle Type"
                value={form.cycle_type}
                onChange={(e) => setForm({ ...form, cycle_type: e.target.value })}
              >
                {["monthly", "bonus", "correction", "arrears", "special"].map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Variation Cut-off"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.variation_cutoff_date || ""}
                onChange={(e) => setForm({ ...form, variation_cutoff_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Payroll Run Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.payroll_run_date || ""}
                onChange={(e) => setForm({ ...form, payroll_run_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Approval Due"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.approval_due_date || ""}
                onChange={(e) => setForm({ ...form, approval_due_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Payment Due"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.payment_due_date || ""}
                onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason / Approval Note"
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={save}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
